import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

const useFetchList = (endpoint) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}${endpoint}`).then(res => setList(res.data)).catch(() => setList([]));
  }, [endpoint]);
  return list;
};

export const AdminFeBaseMasterComponent = () => {
  // Lists for dropdowns and needed data
  const feCodeList = useFetchList('/fe-mgmt/fe-code/');
  const fraudSchemeList = useFetchList('/fe-mgmt/fraudulent-scheme/');
  const businessTypeList = useFetchList('/fe-mgmt/bussiness-type/');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    name_of_fe: '',
    address: '',
    fe_type: '',           // user selects this
    code: '',              // read-only, auto set based on fe_type
    registered_type: '',   // read-only, auto set based on fe_type
    date_of_registration: '',
    date_of_fir: '',
    date_of_charge_sheet: '',
    company_ph: '',
    company_email: '',
    type_of_fraudulent_scheme: '',
    type_of_bussiness: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'name_of_fe', field: 'name_of_fe', headerName: 'Name of FE', width: 200 },
    { id: 'address', field: 'address', headerName: 'Address', width: 200 },
    // { id: 'fe_type', field: 'fe_type', headerName: 'FE Type', width: 150 },
    // { id: 'code', field: 'code', headerName: 'Code', width: 120 },
    // { id: 'registered_type', field: 'registered_type', headerName: 'Registered Type', width: 150 },
    { id: 'date_of_registration', field: 'date_of_registration', headerName: 'Date of Registration', width: 160 },
    { id: 'date_of_fir', field: 'date_of_fir', headerName: 'Date of FIR', width: 160 },
    { id: 'date_of_charge_sheet', field: 'date_of_charge_sheet', headerName: 'Date of Charge Sheet', width: 170 },
    { id: 'company_ph', field: 'company_ph', headerName: 'Company Phone', width: 130 },
    { id: 'company_email', field: 'company_email', headerName: 'Company Email', width: 180 },
    // { id: 'type_of_fraudulent_scheme', field: 'type_of_fraudulent_scheme', headerName: 'Fraudulent Scheme', width: 150 },
    // { id: 'type_of_bussiness', field: 'type_of_bussiness', headerName: 'Business Type', width: 150 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchFeBase();
  }, []);

  // When user selects fe_type, assign code and registered_type automatically
  const handleFeTypeChange = (selectedFeType) => {
    setFormData((prev) => {
      // Find selected fe_code object by fe_type
      const selectedItem = feCodeList.find((item) => item.fe_type === selectedFeType);
      return {
        ...prev,
        fe_type: selectedFeType,
        code: selectedItem ? selectedItem.code : '',
        registered_type: selectedItem ? selectedItem.registered_type : '',
      };
    });
  };

  const fetchFeBase = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then((response) => {
        const apiData = response.data.map((item, index) => ({
          id: item.id,
          slNo: index + 1,
          name_of_fe: item.name_of_fe,
          address: item.address,
          fe_type: item.fe_code?.fe_type || '',            // show fe_type
          code: item.fe_code?.code || '',                   // show code
          registered_type: item.fe_code?.registered_type || '', // show registered_type
          date_of_registration: item.date_of_registration,
          date_of_fir: item.date_of_fir,
          date_of_charge_sheet: item.date_of_charge_sheet,
          company_ph: item.company_ph,
          company_email: item.company_email,
          type_of_fraudulent_scheme: item.type_of_fraudulent_scheme,
          type_of_bussiness: item.type_of_bussiness,
        }));
        setRows(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch FE Base data:', error);
        setSnackbarMessage('Failed to load FE Base data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      name_of_fe: '',
      address: '',
      fe_type: '',
      code: '',
      registered_type: '',
      date_of_registration: '',
      date_of_fir: '',
      date_of_charge_sheet: '',
      company_ph: '',
      company_email: '',
      type_of_fraudulent_scheme: '',
      type_of_bussiness: '',
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    setFormData({
      name_of_fe: item.name_of_fe,
      address: item.address,
      fe_type: item.fe_type,
      code: item.code,
      registered_type: item.registered_type,
      date_of_registration: item.date_of_registration,
      date_of_fir: item.date_of_fir,
      date_of_charge_sheet: item.date_of_charge_sheet,
      company_ph: item.company_ph,
      company_email: item.company_email,
      type_of_fraudulent_scheme: item.type_of_fraudulent_scheme,
      type_of_bussiness: item.type_of_bussiness,
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api
      .delete(`/fe-mgmt/fe-base/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('FE Base deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchFeBase();
      })
      .catch((error) => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete FE Base.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name_of_fe?.trim()) errors.name_of_fe = 'Name of FE is required';
    if (!formData.address?.trim()) errors.address = 'Address is required';
    if (!formData.fe_type) errors.fe_type = 'FE Type is required';
    if (!formData.date_of_registration) errors.date_of_registration = 'Date of Registration is required';
    if (!formData.date_of_fir) errors.date_of_fir = 'Date of FIR is required';
    if (!formData.date_of_charge_sheet) errors.date_of_charge_sheet = 'Date of Charge Sheet is required';
    if (!formData.company_ph?.toString().trim()) errors.company_ph = 'Phone number is required';
    if (!formData.company_email?.trim()) errors.company_email = 'Email is required';
    if (!formData.type_of_fraudulent_scheme) errors.type_of_fraudulent_scheme = 'Scheme is required';
    if (!formData.type_of_bussiness) errors.type_of_bussiness = 'Business type is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    // find fe_code object by fe_type for payload
    const selectedCodeObj = feCodeList.find(item => item.fe_type === formData.fe_type);
    const payload = {
      name_of_fe: formData.name_of_fe,
      address: formData.address,
      fe_code: selectedCodeObj ? selectedCodeObj.id : null,
      date_of_registration: formData.date_of_registration,
      date_of_fir: formData.date_of_fir,
      date_of_charge_sheet: formData.date_of_charge_sheet,
      company_ph: formData.company_ph,
      company_email: formData.company_email,
      type_of_fraudulent_scheme: formData.type_of_fraudulent_scheme,
      type_of_bussiness: formData.type_of_bussiness,
    };
    try {
      setUploading(true);
      setUploadProgress(0);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-base/${selectedItem.id}/`, payload);
        setSnackbarMessage('FE Base updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-base/`, payload);
        setSnackbarMessage('FE Base added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchFeBase();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit FE Base.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center"
        bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">FE Base Master</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>

      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit FE Base' : 'Add FE Base'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name of FE"
            fullWidth
            value={formData.name_of_fe}
            error={!!formErrors.name_of_fe}
            helperText={formErrors.name_of_fe}
            onChange={e => setFormData({ ...formData, name_of_fe: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            value={formData.address}
            error={!!formErrors.address}
            helperText={formErrors.address}
            onChange={e => setFormData({ ...formData, address: e.target.value })}
            disabled={uploading}
          />
          <TextField
            select
            margin="dense"
            label="FE Type"
            fullWidth
            value={formData.fe_type}
            error={!!formErrors.fe_type}
            helperText={formErrors.fe_type}
            onChange={(e) => handleFeTypeChange(e.target.value)}
            disabled={uploading}
          >
            {feCodeList.map(option => (
              <MenuItem key={option.id} value={option.fe_type}>
                {option.fe_type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            value={formData.code}
            InputProps={{
              readOnly: true,
            }}
            disabled
          />
          <TextField
            margin="dense"
            label="Registered Type"
            fullWidth
            value={formData.registered_type}
            InputProps={{
              readOnly: true,
            }}
            disabled
          />
          <TextField
            margin="dense"
            label="Date of Registration"
            type="date"
            fullWidth
            value={formData.date_of_registration}
            error={!!formErrors.date_of_registration}
            helperText={formErrors.date_of_registration}
            onChange={e => setFormData({ ...formData, date_of_registration: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Date of FIR"
            type="date"
            fullWidth
            value={formData.date_of_fir}
            error={!!formErrors.date_of_fir}
            helperText={formErrors.date_of_fir}
            onChange={e => setFormData({ ...formData, date_of_fir: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Date of Charge Sheet"
            type="date"
            fullWidth
            value={formData.date_of_charge_sheet}
            error={!!formErrors.date_of_charge_sheet}
            helperText={formErrors.date_of_charge_sheet}
            onChange={e => setFormData({ ...formData, date_of_charge_sheet: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Company Phone"
            fullWidth
            value={formData.company_ph}
            error={!!formErrors.company_ph}
            helperText={formErrors.company_ph}
            onChange={e => setFormData({ ...formData, company_ph: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Company Email"
            type="email"
            fullWidth
            value={formData.company_email}
            error={!!formErrors.company_email}
            helperText={formErrors.company_email}
            onChange={e => setFormData({ ...formData, company_email: e.target.value })}
            disabled={uploading}
          />
          <TextField
            select
            margin="dense"
            label="Fraudulent Scheme"
            fullWidth
            value={formData.type_of_fraudulent_scheme}
            error={!!formErrors.type_of_fraudulent_scheme}
            helperText={formErrors.type_of_fraudulent_scheme}
            onChange={e => setFormData({ ...formData, type_of_fraudulent_scheme: e.target.value })}
            disabled={uploading}
          >
            {fraudSchemeList.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Business Type"
            fullWidth
            value={formData.type_of_bussiness}
            error={!!formErrors.type_of_bussiness}
            helperText={formErrors.type_of_bussiness}
            onChange={e => setFormData({ ...formData, type_of_bussiness: e.target.value })}
            disabled={uploading}
          >
            {businessTypeList.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                Processing... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this FE Base record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
